import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from 'react';

export interface TurnstileCaptchaRef {
  reset: () => void;
  getResponse: () => string | undefined;
  isExpired: () => boolean;
}

export interface TurnstileCaptchaProps {
  siteKey?: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  className?: string;
  enabled?: boolean;
  onVerify?: (token: string) => void;
  onExpire?: () => void;
  onError?: (errorCode?: string) => void;
  onLoad?: () => void;
}

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Checks if Turnstile CAPTCHA is enabled based on backend auth policy.
 */
export function isTurnstileEnabled(dynamicVal?: boolean): boolean {
  return Boolean(dynamicVal);
}

/**
 * Retrieves the configured Turnstile Site Key from prop or environment variable.
 */
export function getTurnstileSiteKey(customKey?: string): string {
  if (customKey && customKey.trim() !== '' && customKey !== 'undefined') {
    return customKey;
  }
  const envKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY;
  if (envKey && envKey.trim() !== '' && envKey !== 'undefined') {
    return envKey;
  }
  return '';
}

let scriptPromise: Promise<void> | null = null;

export function resetTurnstileScriptPromise(): void {
  scriptPromise = null;
}

/**
 * Loads the Cloudflare Turnstile JavaScript API script safely and idempotently.
 */
export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (!existingScript) {
    scriptPromise = null;
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const currentScript = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (currentScript) {
      if (window.turnstile) {
        return resolve();
      }
      currentScript.addEventListener('load', () => resolve(), { once: true });
      currentScript.addEventListener('error', (err) => reject(err), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => {
      scriptPromise = null;
      reject(err);
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export const TurnstileCaptcha = forwardRef<TurnstileCaptchaRef, TurnstileCaptchaProps>(
  (
    {
      siteKey: propSiteKey,
      action = 'login',
      theme = 'auto',
      size = 'normal',
      className = '',
      enabled: propEnabled,
      onVerify,
      onExpire,
      onError,
      onLoad,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [isScriptLoaded, setIsScriptLoaded] = useState(
      typeof window !== 'undefined' && !!window.turnstile
    );
    const [loadError, setLoadError] = useState<string | null>(null);

    // Keep stable ref to latest callbacks to prevent re-render tearing
    const callbacksRef = useRef({ onVerify, onExpire, onError, onLoad });
    useEffect(() => {
      callbacksRef.current = { onVerify, onExpire, onError, onLoad };
    });

    const enabled = propEnabled !== undefined ? Boolean(propEnabled) : true;
    const siteKey = getTurnstileSiteKey(propSiteKey);

    const reset = useCallback(() => {
      if (typeof window !== 'undefined' && window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // Gracefully ignore reset error if widget is already unmounted
        }
      }
    }, []);

    const getResponse = useCallback(() => {
      if (typeof window !== 'undefined' && window.turnstile && widgetIdRef.current !== null) {
        return window.turnstile.getResponse(widgetIdRef.current);
      }
      return undefined;
    }, []);

    const isExpired = useCallback(() => {
      if (typeof window !== 'undefined' && window.turnstile && widgetIdRef.current !== null) {
        return window.turnstile.isExpired(widgetIdRef.current);
      }
      return false;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        reset,
        getResponse,
        isExpired,
      }),
      [reset, getResponse, isExpired]
    );

    // Load Turnstile script when enabled and siteKey is present
    useEffect(() => {
      if (!enabled || !siteKey) return;

      let isMounted = true;

      loadTurnstileScript()
        .then(() => {
          if (isMounted) {
            setIsScriptLoaded(true);
            callbacksRef.current.onLoad?.();
          }
        })
        .catch((err) => {
          if (isMounted) {
            setLoadError('Failed to load Cloudflare Turnstile');
            callbacksRef.current.onError?.(String(err));
          }
        });

      return () => {
        isMounted = false;
      };
    }, [enabled, siteKey]);

    // Render Turnstile widget once script is loaded and container is ready
    useEffect(() => {
      if (!enabled || !siteKey || !isScriptLoaded || !containerRef.current || !window.turnstile) {
        return;
      }

      // Cleanup any previous widget instance
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore removal error
        }
        widgetIdRef.current = null;
      }

      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          size,
          callback: (token: string) => {
            callbacksRef.current.onVerify?.(token);
          },
          'expired-callback': () => {
            callbacksRef.current.onExpire?.();
          },
          'error-callback': (errorCode?: string) => {
            callbacksRef.current.onError?.(errorCode);
          },
        });
        widgetIdRef.current = id;
      } catch (renderError) {
        callbacksRef.current.onError?.(String(renderError));
      }

      return () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // Ignore cleanup errors on unmount
          }
          widgetIdRef.current = null;
        }
      };
    }, [
      enabled,
      isScriptLoaded,
      siteKey,
      action,
      theme,
      size,
    ]);

    // When CAPTCHA is disabled or siteKey is missing, render nothing
    if (!enabled || !siteKey) {
      return null;
    }

    if (loadError) {
      return (
        <div
          role="alert"
          className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-center"
        >
          Security verification is currently unavailable.
        </div>
      );
    }

    return (
      <div
        className={`flex justify-center my-2 min-h-[65px] ${className}`}
        data-testid="turnstile-container"
      >
        <div ref={containerRef} data-testid="turnstile-widget" />
      </div>
    );
  }
);

TurnstileCaptcha.displayName = 'TurnstileCaptcha';
